import { useId, useState } from 'react'
import { NavLink, useLocation } from 'react-router'
import logoUrl from '@/assets/turonlogowithtext.svg'
import { CaretDownIcon, ChevronLeftIcon, MenuIcon } from '@/components/layout/navIcons'
import {
  matchesNavGate,
  NAV_GROUPS,
  type NavChild,
  type NavGate,
  type NavGroup,
  type NavItem,
} from '@/components/layout/navigation'
import { usePermission } from '@/hooks/usePermission'
import { useSidebarStore } from '@/stores/sidebarStore'
import { cn } from '@/utils/cn'

/**
 * Collapsible sidebar. Collapsed state is client-only UI state in Zustand.
 * Nav items are permission-gated — see `navigation.ts`.
 *
 * Per the design it is a light rail: a brand block, then rows grouped under
 * section headings, with the current row marked by an inset brand-filled pill.
 * The collapse control sits at the foot of the rail, so the Topbar keeps only
 * the drawer trigger it needs below `lg`.
 */
export function Sidebar() {
  const { pathname } = useLocation()
  const isCollapsed = useSidebarStore((state) => state.isCollapsed)
  const isMobileOpen = useSidebarStore((state) => state.isMobileOpen)
  const closeMobile = useSidebarStore((state) => state.closeMobile)
  const toggle = useSidebarStore((state) => state.toggle)
  const { canViewModule, can, isSuperadmin } = usePermission()

  // A row may be gated by a module's `view`, by a sensitive permission, or by
  // both — a child carries the same two fields as its parent. The rule lives in
  // `navigation.ts` because the landing redirect must apply the same one.
  const passes = (gate: NavGate): boolean =>
    matchesNavGate(gate, { can, canViewModule, isSuperadmin })

  /**
   * A CONTAINER (a row with children) owns no screen, so it is judged by its
   * submenu: visible while any child is. Its two children can require different
   * grants — Users needs `user.view`, Roles needs `role.view` — and a
   * role holding either must still see the container.
   */
  const isVisible = (item: NavItem): boolean =>
    item.children ? item.children.some(passes) : passes(item)

  // A group whose rows are all hidden by permissions drops its heading too,
  // rather than leaving a title with nothing under it.
  const groups: readonly NavGroup[] = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(isVisible),
  })).filter((group) => group.items.length > 0)

  return (
    <>
      {/* Off-canvas backdrop on tablet / mobile */}
      {isMobileOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={closeMobile}
          className="fixed inset-0 z-30 bg-[oklch(0_0_0/0.4)] lg:hidden"
        />
      )}

      <nav
        aria-label="Main navigation"
        style={{
          width: isCollapsed
            ? 'var(--layout-sidebar-width-collapsed)'
            : 'var(--layout-sidebar-width)',
        }}
        className={cn(
          'surface-opaque fixed inset-y-0 left-0 z-40 flex shrink-0 flex-col overflow-y-auto border-r border-sidebar-border bg-sidebar transition-transform',
          'lg:static lg:translate-x-0 print:hidden',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <BrandBlock isCollapsed={isCollapsed} onExpand={toggle} />

        <div className="flex-1 px-2 pb-1">
          {groups.map((group) => (
            <NavSection
              key={group.label}
              group={group}
              pathname={pathname}
              isCollapsed={isCollapsed}
              onNavigate={closeMobile}
              isChildVisible={passes}
              onExpandRail={toggle}
            />
          ))}
        </div>

        {/* Collapse lives at the foot of the rail, per the design. Expanded it
            is a labelled row; collapsed the header block carries the control
            instead, so the rail never shows two of them. */}
        {!isCollapsed && (
          <div className="mt-auto border-t border-sidebar-border px-2 py-2">
            <button
              type="button"
              onClick={toggle}
              aria-label="Collapse navigation"
              aria-expanded={true}
              style={{ height: 'var(--layout-sidebar-row-height)' }}
              className="flex w-full items-center gap-3.5 rounded-lg px-3 text-sm text-sidebar-fg transition-colors hover:bg-sidebar-hover hover:text-sidebar-active"
            >
              <ChevronLeftIcon className="size-5 shrink-0" />
              <span>Collapse</span>
            </button>
          </div>
        )}
      </nav>
    </>
  )
}

interface BrandBlockProps {
  readonly isCollapsed: boolean
  readonly onExpand: () => void
}

/**
 * The rail's header: the Turon Travel emblem, matched to the topbar's height so
 * the two chrome blocks line up. Collapsed, it becomes the control that reopens
 * the rail — the only place the expand affordance can live once labels are gone.
 */
function BrandBlock({ isCollapsed, onExpand }: BrandBlockProps) {
  if (isCollapsed) {
    return (
      <button
        type="button"
        onClick={onExpand}
        aria-label="Expand navigation"
        aria-expanded={false}
        style={{ height: 'var(--layout-topbar-height)' }}
        className="group flex shrink-0 items-center justify-center bg-sidebar-header px-2 text-sidebar-fg transition-colors hover:text-sidebar-active"
      >
        {/* Collapsed the rail is 64px wide, which is below this emblem's legible
            size — a menu glyph carries the control instead. */}
        <MenuIcon className="size-6" />
      </button>
    )
  }

  return (
    <div className="flex shrink-0 items-center justify-center bg-sidebar-header px-3">
      <img
        src={logoUrl}
        // A real alt, unlike the login card: here the lockup is the only thing
        // naming the brand — no heading sits beside it.
        alt="Turon Travel"
        width={216}
        height={64}
        // The horizontal lockup (216x64: emblem + wordmark). The width bounds
        // it — the height follows from the ratio, and no vertical padding is
        // added here because the asset already carries its own inner margin.
        className="w-full"
      />
    </div>
  )
}

const ROW_CLASS = 'flex items-center rounded-lg text-sm transition-colors duration-150 ease-out'
const ROW_ACTIVE_CLASS = 'nav-row-active bg-sidebar-active font-medium text-sidebar-active-fg'
const ROW_IDLE_CLASS = 'text-sidebar-fg hover:bg-sidebar-hover hover:text-sidebar-active'

interface NavSectionProps {
  readonly group: NavGroup
  readonly pathname: string
  readonly isCollapsed: boolean
  readonly onNavigate: () => void
  /** Gate for submenu rows — passed down so the two share one rule. */
  readonly isChildVisible: (child: NavChild) => boolean
  /** Reopens the rail: a container has nowhere to put its submenu while collapsed. */
  readonly onExpandRail: () => void
}

/**
 * One titled group of rows. Collapsed, the heading is dropped and a rule stands
 * in for it — there is no width for a label, but the grouping still reads.
 */
function NavSection({
  group,
  pathname,
  isCollapsed,
  onNavigate,
  isChildVisible,
  onExpandRail,
}: NavSectionProps) {
  return (
    <div>
      {isCollapsed ? (
        <div className="mx-2 my-3 border-t border-sidebar-border" role="presentation" />
      ) : (
        <h2 className="px-3 pt-5 pb-2 text-xs font-semibold uppercase tracking-wider text-sidebar-fg">
          {group.label}
        </h2>
      )}

      <ul className="space-y-0.5">
        {group.items.map((item) => {
          const children = (item.children ?? []).filter(isChildVisible)
          // A module's submenu opens while the user is inside that module's
          // section, which is what the design shows for Customers.
          const isSection = item.to !== '/' && pathname.startsWith(item.to)

          return children.length > 0 ? (
            <ContainerRow
              key={item.to}
              item={item}
              children_={children}
              isSection={isSection}
              isCollapsed={isCollapsed}
              onNavigate={onNavigate}
              onExpandRail={onExpandRail}
            />
          ) : (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === '/'}
                onClick={onNavigate}
                title={isCollapsed ? item.label : undefined}
                style={{ height: 'var(--layout-sidebar-row-height)' }}
                className={({ isActive }) =>
                  cn(
                    ROW_CLASS,
                    // Expanded, the icon sits in from the row's left edge — the
                    // row's own fill still spans the full rail width, so it is
                    // padding inside the row, not a narrower row.
                    isCollapsed ? 'justify-center' : 'gap-3.5 pr-3 pl-5',
                    isActive || isSection ? ROW_ACTIVE_CLASS : ROW_IDLE_CLASS,
                  )
                }
              >
                {/* Icon and label inherit the row's colour so the two never
                    drift apart — the row owns the active/inactive tone,
                    nothing inside it sets its own. */}
                <item.icon className="size-5 shrink-0" />
                {!isCollapsed && <span className="min-w-0 flex-1 truncate">{item.label}</span>}
              </NavLink>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

interface ContainerRowProps {
  readonly item: NavItem
  /** Already filtered by permission. Named with a trailing _ to avoid React's `children`. */
  readonly children_: readonly NavChild[]
  /** True while the route sits inside this container's base path. */
  readonly isSection: boolean
  readonly isCollapsed: boolean
  readonly onNavigate: () => void
  readonly onExpandRail: () => void
}

/**
 * A container row — one that owns no screen of its own, only a submenu
 * (System > User Management).
 *
 * It is a **button, not a link**: clicking it opens and closes its submenu and
 * navigates nowhere (client instruction, 2026-09-15). The screens live on the
 * children, so sending the user to one of them on a click meant for "show me
 * what is in here" was choosing for them.
 *
 * Open state defaults to whether the route is inside the section, so arriving
 * at a child screen finds the submenu already open; once the user toggles it,
 * their choice wins for the rest of the session.
 *
 * The row never takes the active fill, per the design: the highlighted pill
 * belongs to the child that is actually open.
 */
function ContainerRow({
  item,
  children_,
  isSection,
  isCollapsed,
  onNavigate,
  onExpandRail,
}: ContainerRowProps) {
  const submenuId = useId()
  /** null = follow the route; true/false = the user's own choice. */
  const [override, setOverride] = useState<boolean | null>(null)
  const isOpen = override ?? isSection

  return (
    <li>
      <button
        type="button"
        // Collapsed, the rail is icons only and there is nowhere to draw a
        // submenu — so the row reopens the rail instead of toggling into
        // something the user cannot see.
        onClick={() => (isCollapsed ? onExpandRail() : setOverride(!isOpen))}
        aria-expanded={isCollapsed ? undefined : isOpen}
        aria-controls={isCollapsed ? undefined : submenuId}
        title={isCollapsed ? item.label : undefined}
        style={{ height: 'var(--layout-sidebar-row-height)' }}
        className={cn(
          ROW_CLASS,
          'w-full',
          isCollapsed ? 'justify-center' : 'gap-3.5 pr-3 pl-5',
          // Never the active pill: that belongs to the open child.
          ROW_IDLE_CLASS,
          isOpen && !isCollapsed && 'text-sidebar-active',
        )}
      >
        <item.icon className="size-5 shrink-0" />
        {!isCollapsed && (
          <>
            <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
            {/* Points down while shut, up while open — the design's caret. */}
            <CaretDownIcon
              aria-hidden="true"
              className={cn('size-3.5 shrink-0 transition-transform', isOpen && 'rotate-180')}
            />
          </>
        )}
      </button>

      {/* Collapsed, the rail is icons only — there is nowhere to put a submenu.

          Expanded, the submenu stays mounted and animates its height open and
          shut, so entering a module glides instead of the rows snapping in and
          shoving the rest of the nav down. `inert` keeps the closed rows out of
          the tab order. */}
      {!isCollapsed && (
        <div
          id={submenuId}
          className={cn('nav-submenu grid', isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}
        >
          <div className="overflow-hidden">
            <SubMenu items={children_} onNavigate={onNavigate} isOpen={isOpen} />
          </div>
        </div>
      )}
    </li>
  )
}

interface SubMenuProps {
  readonly items: readonly NavChild[]
  readonly onNavigate: () => void
  /** Closed rows are inert: not focusable, not read out, not clickable. */
  readonly isOpen: boolean
}

/**
 * The open module's submenu: indented rows under the parent, tracked by a rule
 * down the left so they read as belonging to the row above them.
 */
function SubMenu({ items, onNavigate, isOpen }: SubMenuProps) {
  return (
    <ul inert={!isOpen} className="mt-0.5 ml-9 space-y-0.5 border-l border-sidebar-border pl-3">
      {items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            end
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'block rounded-lg px-3 py-2 text-sm transition-colors',
                isActive
                  ? 'font-medium text-sidebar-active'
                  : 'text-sidebar-fg hover:bg-sidebar-hover hover:text-sidebar-active',
              )
            }
          >
            {item.label}
          </NavLink>
        </li>
      ))}
    </ul>
  )
}
